// Pure helpers for the server-side timer (useServerTimer.js holds the state).
//
// The server clock is authoritative. Each response carries `serverNow`; the
// offset `serverNow − Date.now()` turns this browser's "now" into the
// server's (serverNow below), so the elapsed counter and the calendar ghost
// stay right even when the machine's clock is off. The server instants
// themselves are kept as they are: the Start/End fields show them on the wall
// clock. Edited Start/End times go back as the "HH:MM" typed — wall-clock in
// the timer's zone, which the server resolves.

import { fmtTime } from './time.js';

export function clockOffset(serverNowIso, clientNowMs = Date.now()) {
  const serverMs = Date.parse(serverNowIso);
  return Number.isFinite(serverMs) ? serverMs - clientNowMs : 0;
}

const toDate = (iso) => (iso ? new Date(iso) : null);

// The wire timer → the shape the app uses: the server's instants as Dates,
// plus the clock offset (for serverNow). Keeps the fields the calendar ghost
// has always read (status, projectId, segments, activeStart).
export function deserializeTimer(raw, offsetMs) {
  if (!raw) return null;
  return {
    id: raw.id,
    status: raw.status,
    projectId: raw.projectId || '',
    projectCode: raw.projectCode,
    projectName: raw.projectName,
    description: raw.description || '',
    timezone: raw.timezone,
    segments: (raw.segments || []).map((segment) => ({
      start: toDate(segment.start),
      end: toDate(segment.end),
    })),
    activeStart: toDate(raw.activeStart),
    autoPausedAt: toDate(raw.autoPausedAt),
    pendingEntries: raw.pendingEntries || [],
    offsetMs: offsetMs || 0,
  };
}

// This browser's `clientNow` on the server's clock — the "now" to measure the
// timer's instants against (elapsed time, reminder, the ghost's open edge).
export function serverNow(timer, clientNow = new Date()) {
  return new Date(clientNow.getTime() + (timer?.offsetMs || 0));
}

const zoneFormats = new Map();

// The time of day `date` shows on the wall clock in `timezone` (the timer's
// zone, not this browser's), as decimal hours for a TimeField.
export function zonedHours(date, timezone) {
  if (!zoneFormats.has(timezone)) {
    zoneFormats.set(
      timezone,
      new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
    );
  }
  const parts = Object.fromEntries(
    zoneFormats
      .get(timezone)
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return Number(parts.hour) + Number(parts.minute) / 60;
}

// A TimeField's decimal hours → the "HH:MM" sent for a typed Start/End. The
// server reads it in the timer's zone: a Start on the day the timer started,
// an End on the day the last stretch started (the next day when not after
// it — so an End of 24:00 is sent as 00:00, which rolls over). A Start of
// 24:00 has no such day to roll to: null, not sent.
export function clockText(hours, { end = false } = {}) {
  if (hours >= 24 && !end) return null;
  return fmtTime(hours % 24);
}

// POST /timer/save carries the dialog's current values explicitly, so an edit
// still on its way can't be lost.
export function saveTimerBody({ projectId, description, startAt, endAt }) {
  return {
    projectId,
    description,
    startAt: startAt || undefined,
    endAt: endAt || undefined,
  };
}

// The timer actions as requests: `send(path, method, body)`. Every one but
// start names the timer it acts on (`timerId()`, read when the action is
// called — the timer the user clicked on), so the server refuses it with 409
// once another tab has replaced that timer. A debounced update passes the id
// it was typed on (`{ timerId }`) instead, since it is sent later. Start/End
// edits are the typed "HH:MM" (see clockText).
export function timerActions(send, timerId) {
  return {
    start: ({ projectId, description } = {}) =>
      send('/timer/start', 'POST', {
        projectId: projectId || null,
        description: description || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    pause: () => send('/timer/pause', 'POST', { timerId: timerId() }),
    resume: () => send('/timer/resume', 'POST', { timerId: timerId() }),
    stop: ({ endAt } = {}) =>
      send('/timer/stop', 'POST', {
        timerId: timerId(),
        endAt: endAt || undefined,
      }),
    update: (
      { projectId, description, startAt, endAt },
      { timerId: id = timerId() } = {},
    ) =>
      send('/timer', 'PATCH', {
        timerId: id,
        ...(projectId !== undefined ? { projectId: projectId || null } : {}),
        ...(description !== undefined ? { description } : {}),
        startAt: startAt || undefined,
        endAt: endAt || undefined,
      }),
    save: (values) =>
      send('/timer/save', 'POST', {
        timerId: timerId(),
        ...saveTimerBody(values),
      }),
    discard: () => {
      const id = timerId();
      return send(
        id ? `/timer?timerId=${encodeURIComponent(id)}` : '/timer',
        'DELETE',
      );
    },
  };
}

// Delays an edit while the user is still typing, then `send`s it (a promise).
// cancel() drops it; takePending() drops it and hands back its value — or,
// once sent, the value whose request is still in flight, which the server may
// not have yet — undefined when none. A value is handed back once: a later
// takePending() doesn't carry it again. version() counts schedule() calls, so a
// caller can tell a newer edit came in. isIdle(): nothing waiting or in flight.
export function createEditQueue({
  send,
  delayMs,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
}) {
  let pending = null;
  let inflight = null;
  let version = 0;
  return {
    schedule(value) {
      version += 1;
      clearTimer(pending?.id);
      const id = setTimer(() => {
        pending = null;
        const flight = { value };
        inflight = flight;
        Promise.resolve(send(value)).finally(() => {
          if (inflight === flight) inflight = null;
        });
      }, delayMs);
      pending = { id, value };
    },
    cancel() {
      clearTimer(pending?.id);
      pending = null;
      inflight = null;
    },
    takePending() {
      const value = pending ? pending.value : inflight?.value;
      clearTimer(pending?.id);
      pending = null;
      inflight = null;
      return value;
    },
    isPending: () => pending != null,
    isIdle: () => pending == null && inflight == null,
    version: () => version,
  };
}

// A taken Start edit ({ value, timerId }) goes with Save only to the timer it
// was typed on; one typed on a timer since replaced is dropped, not sent.
export function pendingEditFor(edit, timerId) {
  return edit && edit.timerId === timerId ? edit : undefined;
}

// Identifies the stop a save dialog's End edit belongs to: the timer
// generation and its final stretch. A change means the edit is stale. Not the
// final stretch's start: on a one-segment stop that is the overall start,
// which this tab's own Start edit moves.
export function endEditKey(timer) {
  const last = timer?.segments[timer.segments.length - 1];
  return [timer?.id, timer?.segments.length, last?.end?.getTime()].join('|');
}

// Save with the Start edit still waiting in its debounce: the edit goes along
// with Save, and is put back when Save doesn't go through (refused, failed or
// thrown), so the time the Start field shows isn't lost. `send` resolves to a
// falsy value on failure, or throws; either way the result is passed on.
// `editVersion` changes with every Start edit: one made while Save was in
// flight is newer than the taken value, so that value is not put back over it.
export async function saveWithPendingStart({
  takePending,
  requeue,
  editVersion,
  send,
}) {
  const version = editVersion();
  const startAt = takePending();
  let saved = null;
  try {
    saved = await send(startAt);
    return saved;
  } finally {
    if (!saved && startAt !== undefined && editVersion() === version)
      requeue(startAt);
  }
}

// Responses can land out of order (a debounced description save racing a
// pause, or a broadcast from another tab); never let an older snapshot
// overwrite a newer one.
export function isNewer(serverNowIso, lastAppliedIso) {
  if (!lastAppliedIso) return true;
  return Date.parse(serverNowIso) >= Date.parse(lastAppliedIso);
}

export function elapsedMs(timer, now) {
  if (!timer) return 0;
  const saved = timer.segments.reduce(
    (sum, segment) => sum + (segment.end.getTime() - segment.start.getTime()),
    0,
  );
  return (
    saved +
    (timer.activeStart ? Math.max(0, now.getTime() - timer.activeStart) : 0)
  );
}

// The open stretch has run past the reminder limit — probably forgotten.
export function isRunningLong(timer, now, limits) {
  if (!timer || timer.status !== 'running' || !timer.activeStart || !limits)
    return false;
  return now - timer.activeStart >= limits.reminderHours * 3600 * 1000;
}
